import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Actions, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { cloneDeep } from "lodash-es";
import { BehaviorSubject, Observable, Subject, Subscription, takeUntil } from "rxjs";
import { CancellationError } from "../confirm/confirm-dialog.service";
import { RootState } from '../root-store/root.state';
import { selectLastClickedTab } from '../se-side/redux/se-side-nav.selectors';
import { SeSideComponentType } from '../se-side/se-side-components/se-side-components-models';
import { SeSidePatientHistoryComponentActions, seSidePatientHistoryComponentNoResult, seSidePatientHistoryComponentSetPatient } from "../se-side/se-side-components/se-side-patient-history/redux/se-side-patient-history.actions";
import { SearchFormType } from "../search-page/search-page-type.enum";
import { NotificationService } from '../services/notification.service';
import { IEprEventsWithErrorMessage, PatientHistoryService } from "../services/patient-history.service";
import { PatientReportHistoryService } from '../services/rest/patient-report-history.service';
import { fromServerIsoString } from "../utils/interop";
import { seTimelineComponentSetData, seTimelineComponentSetFilters, seTimelineComponentSetReport, seTimelineComponentSetReportFilters } from "./redux/se-timeline.actions";
import { selectSeTimelineData } from './redux/se-timeline.selectors';
import { SeTimelineDetailsComponent } from './se-timeline-details/se-timeline-details.component';
import { SeTimelineFiltersComponent } from './se-timeline-filters/se-timeline-filters.component';
import { ExaminationPatientHistoryTimeline, PastFutureInfo, TimelineFilters, YearData, YearMonthsData } from "./se-timeline.models";

@Component({
    selector: 'se-timeline',
    templateUrl: './se-timeline.component.html',
    styleUrls: ['./se-timeline.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        TranslateModule,
        SeTimelineDetailsComponent,
        SeTimelineFiltersComponent,
        MatTooltipModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeTimelineComponent implements OnInit, OnDestroy {
    private reportHistoryService: PatientReportHistoryService = inject(PatientReportHistoryService);
    private patientHistoryService: PatientHistoryService = inject(PatientHistoryService);
    private notificationService: NotificationService = inject(NotificationService);
    private translateService: TranslateService = inject(TranslateService);
    private store: Store<RootState> = inject(Store<RootState>);

    clinicalHistory: any[] = [];
    defaultIconName: string = 'Generic_Default';
    filters: TimelineFilters = { min: null, max: null, isMonthlyView: false };
    formType!: SearchFormType;
    futureVisits: any[] = [];
    groupedClinicalHistoryData: YearMonthsData[] = [];
    groupedReportHistoryData: YearMonthsData[] = [];
    groupedClinicalHistoryDataClone: YearMonthsData[] = [];
    groupedReportHistoryDataClone: YearMonthsData[] = [];
    isLoading = new BehaviorSubject<boolean>(false);
    lastTimelineMode = signal<string | null>(null);
    selectedPatientId: string = '';
    showInfo: PastFutureInfo = { past: false, future: false };
    timelineMessage: string = this.translateService.instant('TimelineNoResults');
    reportHistory!: Observable<ExaminationPatientHistoryTimeline[]>;

    private actionsSubscription!: Subscription;
    private dataUpdateSubscription!: Subscription;
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    constructor(private actions: Actions) {
        this.reportHistory = toObservable(this.reportHistoryService.filteredExaminationsByModalities);
        this.store.select(selectLastClickedTab)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(tab => {
                if (tab === SeSideComponentType.patientHistory) {
                    this.lastTimelineMode.set('clinical');
                } else if (tab === SeSideComponentType.reportHistory) {
                    this.lastTimelineMode.set('report');
                }
            });
    }

    ngOnInit(): void {
        this.listenForStoreEvents();

        this.isLoading.next(true);
        this.reportHistory
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((reports) => {
                this.isLoading.next(false);
                if (reports && reports.length > 0) {
                    this.groupedReportHistoryData = this.groupVisitsByMonth(reports);
                    this.groupedReportHistoryDataClone = cloneDeep(this.groupedReportHistoryData);
                    this.store.dispatch(seTimelineComponentSetReport({ report: this.groupedReportHistoryDataClone }));
                    let filters = { min: null, max: null, isMonthlyView: false };
                    this.store.dispatch(seTimelineComponentSetReportFilters({ filters: filters }));
                } else {
                    this.groupedReportHistoryData = [];
                    this.groupedReportHistoryDataClone = [];
                }
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        if (this.actionsSubscription) {
            this.actionsSubscription.unsubscribe();
        }
        if (this.dataUpdateSubscription) {
            this.dataUpdateSubscription.unsubscribe();
        }
    }

    private mapDates(arrayOfObjects: ExaminationPatientHistoryTimeline[] | any[]) {
        return arrayOfObjects.map(obj => {
            return {
                ...obj,
                date: new Date(obj.date || obj.executionDate)
            };
        });
    }

    private checkIconsOrAddDefault(visit: any) {
        if (!visit.iconName || visit.iconName == '') {
            visit.iconName = this.defaultIconName;
        }
    }

    private groupVisitsByMonth(dataArray: ExaminationPatientHistoryTimeline[] | any[]): YearMonthsData[] {
        let groupedByYear = this.groupVisitsByYear(dataArray);
        let result: any[] = [];

        if (groupedByYear.length >= 1) {
            groupedByYear.forEach((yearData: YearData) => {
                const year = yearData.year;
                const visits = yearData.visits;
                const groupedByMonth: { [month: string]: any[]; } = {};

                visits.forEach((visit: any) => {
                    this.checkIconsOrAddDefault(visit);
                    const month = visit.date?.getMonth();
                    if (!groupedByMonth[month]) {
                        groupedByMonth[month] = [];
                    }
                    groupedByMonth[month].push(visit);
                });

                const yearResult = {
                    year: year,
                    months: Object.entries(groupedByMonth).map(([month, visits]) => ({
                        month: +month + 1, //converto in num e aggiungo 1
                        visits: visits
                    }))
                };

                result.push(yearResult);
            });
        } else {
            result = [];
        }
        return result;
    }

    private groupVisitsByYear(dataArray: ExaminationPatientHistoryTimeline[] | any[]): YearData[] {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let newArray = this.mapDates(dataArray);
        newArray.sort((a: any, b: any) => a.date.getTime() - b.date.getTime());

        const pastGroupedData: { [year: string]: any[]; } = {};
        const futureVisits: any[] = [];

        newArray.forEach((item: any) => {
            const visitDate = new Date(item.date);
            visitDate.setHours(0, 0, 0, 0);
            const year = visitDate.getFullYear().toString();

            if (visitDate < today || visitDate.getTime() === today.getTime()) {

                if (!pastGroupedData[year]) {
                    pastGroupedData[year] = [];
                }
                pastGroupedData[year].push(item);
            } else {
                futureVisits.push(item);
            }
        });

        this.futureVisits = futureVisits;
        const pastVisits = Object.entries(pastGroupedData).map(([year, visits]) => ({
            year,
            visits
        }));

        return pastVisits;
    }

    private listenForStoreEvents() {
        this.actionsSubscription = this.actions.pipe(ofType(
            seSidePatientHistoryComponentSetPatient,
            seSidePatientHistoryComponentNoResult)).subscribe(
                async a => {
                    const notAvailableMsg = this.translateService.instant('TimelineNotAvailable');
                    const noResultMsg = this.translateService.instant('TimelineNoResults');
                    const notCompleteMsg = this.translateService.instant('TimelineNotComplete');
                    this.isLoading.next(true);
                    let tmpClinicalHistory = cloneDeep(this.clinicalHistory);
                    this.clinicalHistory = [];
                    this.formType = a.formType;

                    switch (a.type) {
                        case SeSidePatientHistoryComponentActions.SET_PATIENT:
                            if (!a.patientId) {
                                this.timelineMessage = noResultMsg;
                                this.isLoading.next(false);
                                return;
                            }

                            this.selectedPatientId = a.patientId;
                            this.timelineMessage = this.translateService.instant('Loading');
                            //this.store.dispatch(seToolbarSetTimeline({ status: TIMELINE_OPTIONS.TIMELINE_OPEN }));

                            try {
                                let isAllBuSearch: boolean = a.isAllBuSearch || false;
                                this.patientHistoryService.getAllEprEvents(a.patientId.toString(), isAllBuSearch, a.credentials)
                                    .pipe(takeUntil(this.ngUnsubscribe)) // blocca le eventuali chiamate in corso
                                    .subscribe({
                                        next: (result: IEprEventsWithErrorMessage) => {
                                            this.clinicalHistory = [];

                                            if (result.eprEvents && result.eprEvents.length > 0) {
                                                this.timelineMessage = '';
                                                result.eprEvents.forEach((event) => {
                                                    const dateString = this.formatDate(event.date);
                                                    const reportDateString = this.formatDate(event.reportDate);
                                                    this.clinicalHistory.push({ ...event, selected: false, reportDateString: reportDateString, dateString: dateString });
                                                });

                                                this.groupedClinicalHistoryData = this.groupVisitsByMonth(this.clinicalHistory);

                                                if (this.groupedClinicalHistoryData.length !== 0) {
                                                    this.groupedClinicalHistoryDataClone = cloneDeep(this.groupedClinicalHistoryData);
                                                    this.store.dispatch(seTimelineComponentSetData({ data: this.groupedClinicalHistoryDataClone }));
                                                } else {
                                                    this.groupedClinicalHistoryDataClone = [];
                                                }

                                                let filters = { min: null, max: null, isMonthlyView: false };
                                                this.store.dispatch(seTimelineComponentSetFilters({ filters: filters }));
                                                this.showInfo = { past: false, future: false };

                                                // Se uno dei due messaggi di errore è presente, notifico
                                                if (!!result.displayMessage || !!result.exceptionMessage) {
                                                    let message: string = '';
                                                    message += result.displayMessage || '';
                                                    message += result.exceptionMessage ? ` - ${result.exceptionMessage}` : '';

                                                    // Non ci sono risultati - Notifica warning
                                                    this.notificationService.warning(message, notCompleteMsg);
                                                }
                                            } else {
                                                this.timelineMessage = noResultMsg;
                                            }
                                            this.isLoading.next(false);
                                        },
                                        error: err => {
                                            this.clinicalHistory = tmpClinicalHistory;
                                            this.handleError(err, notAvailableMsg, noResultMsg);
                                        }
                                    });
                            } catch (error) {
                                this.handleError(error, notAvailableMsg, noResultMsg);
                            }
                            break;
                        case SeSidePatientHistoryComponentActions.NO_RESULT:
                            this.ngUnsubscribe.next();
                            this.timelineMessage = a.message;
                            this.clinicalHistory = [];
                            this.groupedClinicalHistoryData = [];
                            this.groupedClinicalHistoryDataClone = [];
                            this.isLoading.next(false);
                            break;

                        default:
                            this.timelineMessage = noResultMsg;
                            this.isLoading.next(false);
                            break;
                    }
                }
            );

        this.dataUpdateSubscription = this.store
            .pipe(select(selectSeTimelineData)).subscribe({
                next: (data) => {
                    if (!!data) {
                        this.groupedClinicalHistoryDataClone = data;
                    }
                },
                error: (error) => { throw new Error(error); }
            });
    }

    private handleError(error: any, notAvailableMsg: string, noResultMsg: string): void {
        if (error?.data?.exceptionTypeName != 'Estensa.Web.Exceptions.CredentialsFault') {
            this.timelineMessage = noResultMsg;
            this.clinicalHistory = [];
            this.groupedClinicalHistoryData = [];
            if (!(error instanceof CancellationError)) {
                this.notificationService.warning(error.message ?? '', notAvailableMsg);
            }
        }
        else {
            // messaggio della credenziali
            let errorMessage = this.translateService.instant('Exception_CredentialsFault') +
                ((error?.data?.exceptionMessage) ? ": " + error.data.exceptionMessage : '');
            this.notificationService.warning(errorMessage);
            this.store.dispatch(seTimelineComponentSetData({ data: this.groupedClinicalHistoryDataClone }));
            let filters = { min: null, max: null, isMonthlyView: false };
            this.store.dispatch(seTimelineComponentSetFilters({ filters: filters }));
        }

        this.isLoading.next(false);
    }

    private formatDate(date: string | Date | null) {
        if (!date) {
            return null;
        }
        if (date instanceof Date) {
            date = date.toJSON();
        }
        return fromServerIsoString(date)
            .toDate()
            .toLocaleDateString(undefined, { hour: '2-digit', minute: '2-digit' });
    }

    _toggleShowInfo(event: PastFutureInfo) {
        this.showInfo = event;
    }

    _onFiltersChange(filters: TimelineFilters) {
        this.filters = cloneDeep(filters);
    }
}
